'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { signupAction } from '@/app/(auth)/signup/actions';
import Link from 'next/link';

const signupSchema = z
  .object({
    email: z
      .string()
      .transform((v) => v.trim())
      .pipe(z.string().email('Invalid email format')),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Must contain at least one number'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type SignupFormData = z.input<typeof signupSchema>;

function mapSignupError(message: string): string {
  if (message.includes('User already registered')) {
    return 'Email already registered';
  }
  return message;
}

interface SignupFormProps {
  redirectTo: string;
}

export function SignupForm({ redirectTo }: SignupFormProps) {
  const router = useRouter();
  const [serverError, setServerError] = useState<string | null>(null);
  const [confirmationSent, setConfirmationSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    mode: 'onTouched',
  });

  async function onSubmit(data: SignupFormData) {
    setServerError(null);
    // zodResolver applies transforms — data.email is already trimmed
    const result = await signupAction(data.email as string, data.password);
    if (result.error) {
      setServerError(mapSignupError(result.error));
      return;
    }
    if (result.needsConfirmation) {
      setConfirmationSent(true);
      return;
    }
    router.push(redirectTo);
  }

  if (confirmationSent) {
    return (
      <div className="text-center">
        <p className="mb-2 text-lg font-medium" style={{ color: '#fff' }}>Check your email</p>
        <p className="mb-4 text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
          We sent a confirmation link to your email address. Click the link to
          activate your account.
        </p>
        <Link href="/login" className="text-sm">
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} onChange={() => setServerError(null)} aria-describedby={serverError ? 'server-error' : undefined}>
      {serverError && (
        <p id="server-error" role="alert" className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2 text-sm text-red-300">
          {serverError}
        </p>
      )}

      <div className="mb-4">
        <label htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          aria-describedby={errors.email ? 'email-error' : undefined}
          {...register('email')}
        />
        {errors.email && (
          <p id="email-error" role="alert" className="mt-1.5 text-sm text-red-400">
            {errors.email.message}
          </p>
        )}
      </div>

      <div className="mb-4">
        <label htmlFor="password">Password</label>
        <input
          id="password"
          type="password"
          autoComplete="new-password"
          placeholder="••••••••"
          aria-describedby={errors.password ? 'password-error' : undefined}
          {...register('password')}
        />
        {errors.password && (
          <p id="password-error" role="alert" className="mt-1.5 text-sm text-red-400">
            {errors.password.message}
          </p>
        )}
      </div>

      <div className="mb-6">
        <label htmlFor="confirmPassword">Confirm password</label>
        <input
          id="confirmPassword"
          type="password"
          autoComplete="new-password"
          placeholder="••••••••"
          aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
          {...register('confirmPassword')}
        />
        {errors.confirmPassword && (
          <p id="confirmPassword-error" role="alert" className="mt-1.5 text-sm text-red-400">
            {errors.confirmPassword.message}
          </p>
        )}
      </div>

      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Signing up...' : 'Sign up'}
      </button>

      <p className="mt-5 text-center text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
        Already have an account?{' '}
        <Link href="/login">Sign in</Link>
      </p>
    </form>
  );
}
